<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { fade, fly, scale } from 'svelte/transition';
	import { onMount } from 'svelte';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	let visible = $state(true); // SSR: start visible so hero + <h1> are in server HTML (Ahrefs: "H1 tag missing"). onMount re-set is a no-op; entrance animations still play on client-side navigation.
	let currentStep = $state(0);

	onMount(() => {
		visible = true;
		// Animate through steps
		const interval = setInterval(() => {
			currentStep = (currentStep + 1) % 4;
		}, 3000);
		return () => clearInterval(interval);
	});

	const workflow = [
		{
			icon: 'clock',
			title: $_('features.invoicing.workflow.step1.title'),
			description: $_('features.invoicing.workflow.step1.description')
		},
		{
			icon: 'file-text',
			title: $_('features.invoicing.workflow.step2.title'),
			description: $_('features.invoicing.workflow.step2.description')
		},
		{
			icon: 'bell',
			title: $_('features.invoicing.workflow.step3.title'),
			description: $_('features.invoicing.workflow.step3.description')
		},
		{
			icon: 'dollar-sign',
			title: $_('features.invoicing.workflow.step4.title'),
			description: $_('features.invoicing.workflow.step4.description')
		}
	];

	const painPoints = {
		title: $_('features.invoicing.problems.title'),
		problems: [
			{
				icon: 'x',
				text: $_('features.invoicing.problems.problem1')
			},
			{
				icon: 'x',
				text: $_('features.invoicing.problems.problem2')
			},
			{
				icon: 'x',
				text: $_('features.invoicing.problems.problem3')
			},
			{
				icon: 'x',
				text: $_('features.invoicing.problems.problem4')
			}
		]
	};

	const features = [
		{
			title: $_('features.invoicing.features.feature1.title'),
			description: $_('features.invoicing.features.feature1.description'),
			icon: 'settings'
		},
		{
			title: $_('features.invoicing.features.feature2.title'),
			description: $_('features.invoicing.features.feature2.description'),
			icon: 'credit-card'
		},
		{
			title: $_('features.invoicing.features.feature3.title'),
			description: $_('features.invoicing.features.feature3.description'),
			icon: 'bell'
		}
	];
</script>

<svelte:head>
	<title>{$_('features.invoicing.meta.title')}</title>
	<meta name="description" content={$_('features.invoicing.meta.description')} />
	<meta property="og:title" content="{$_('features.invoicing.meta.title')}" />
	<meta property="og:description" content={$_('features.invoicing.meta.description')} />
</svelte:head>

<Header />

<div class="feature-page">
	<!-- Hero Section - Direct and clear -->
	<section class="hero">
		<div class="container">
			{#if visible}
				<div class="hero__split">
					<div class="hero__content" in:fade={{ duration: 600, delay: 100 }}>
						<div class="hero__eyebrow" in:fly={{ y: 20, duration: 500, delay: 200 }}>
							<Icon name="file-text" size={20} />
							<span>{$_('features.invoicing.hero.eyebrow')}</span>
						</div>
						<h1 class="hero__title" in:fly={{ y: 30, duration: 600, delay: 300 }}>
							{@html $_('features.invoicing.hero.title')}
						</h1>
						<p class="hero__subtitle" in:fly={{ y: 30, duration: 600, delay: 400 }}>
							{$_('features.invoicing.hero.subtitle')}
						</p>
						<div class="hero__stats" in:fly={{ y: 30, duration: 600, delay: 500 }}>
							<div class="stat">
								<div class="stat__number">{$_('features.invoicing.hero.stat1.number')}</div>
								<div class="stat__label">{$_('features.invoicing.hero.stat1.label')}</div>
							</div>
							<div class="stat__divider"></div>
							<div class="stat">
								<div class="stat__number">{$_('features.invoicing.hero.stat2.number')}</div>
								<div class="stat__label">{$_('features.invoicing.hero.stat2.label')}</div>
							</div>
						</div>
						<div class="hero__actions" in:fly={{ y: 30, duration: 600, delay: 600 }}>
							<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
								{$_('features.invoicing.hero.cta')}
							</Button>
						</div>
					</div>
					<div class="hero__visual" in:scale={{ duration: 800, delay: 400 }}>
						<div class="invoice-preview">
							<div class="invoice-preview__header">
								<div class="invoice-preview__logo"></div>
								<div class="invoice-preview__number">
									{$_('features.invoicing.preview.invoiceNumber')}
								</div>
							</div>
							<div class="invoice-preview__client">
								<div class="invoice-preview__line"></div>
								<div class="invoice-preview__line short"></div>
							</div>
							<div class="invoice-preview__items">
								<div class="invoice-preview__item"></div>
								<div class="invoice-preview__item"></div>
								<div class="invoice-preview__item"></div>
							</div>
							<div class="invoice-preview__total">
								<div class="invoice-preview__total-label">
									{$_('features.invoicing.preview.total')}
								</div>
								<div class="invoice-preview__total-amount">
									{$_('features.invoicing.preview.amount')}
								</div>
							</div>
							<div class="invoice-preview__pay-button">
								<Icon name="credit-card" size={16} />
								{$_('features.invoicing.preview.payNow')}
							</div>
						</div>
					</div>
				</div>
			{/if}
		</div>
	</section>

	<!-- Workflow Section -->
	<section class="workflow">
		<div class="container">
			<div class="workflow__header">
				<h2 class="workflow__title">{$_('features.invoicing.workflow.title')}</h2>
				<p class="workflow__subtitle">{$_('features.invoicing.workflow.subtitle')}</p>
			</div>
			<div class="workflow__steps">
				{#each workflow as step, index}
					<div class="workflow__step" class:active={currentStep === index}>
						<div class="workflow__step-icon">
							<Icon name={step.icon} size={24} />
						</div>
						<h3 class="workflow__step-title">{step.title}</h3>
						<p class="workflow__step-description">{step.description}</p>
						{#if index < workflow.length - 1}
							<div class="workflow__arrow">
								<Icon name="arrow-right" size={20} />
							</div>
						{/if}
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Problem Section -->
	<section class="problems">
		<div class="container">
			<div class="problems__content">
				<div class="problems__left">
					<h2 class="problems__title">{painPoints.title}</h2>
					<div class="problems__list">
						{#each painPoints.problems as problem}
							<div class="problem-item">
								<Icon name={problem.icon} size={20} style="color: var(--text-error);" />
								<span>{problem.text}</span>
							</div>
						{/each}
					</div>
				</div>
				<div class="problems__right">
					<div class="solution-card">
						<h3 class="solution-card__title">{$_('features.invoicing.solution.title')}</h3>
						<ul class="solution-card__list">
							<li>{$_('features.invoicing.solution.point1')}</li>
							<li>{$_('features.invoicing.solution.point2')}</li>
							<li>{$_('features.invoicing.solution.point3')}</li>
							<li>{$_('features.invoicing.solution.point4')}</li>
						</ul>
						<Button variant="secondary" size="md" href="#features">
							{$_('features.invoicing.solution.cta')}
						</Button>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Features Section -->
	<section class="features" id="features">
		<div class="container">
			<div class="features__grid">
				{#each features as feature}
					<div class="feature-block">
						<div class="feature-block__icon">
							<Icon name={feature.icon} size={32} />
						</div>
						<h3 class="feature-block__title">{feature.title}</h3>
						<p class="feature-block__description">{feature.description}</p>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Use Cases -->
	<section class="use-cases">
		<div class="container">
			<div class="use-cases__header">
				<h2 class="use-cases__title">{$_('features.invoicing.useCases.title')}</h2>
			</div>
			<div class="use-cases__grid">
				<div class="use-case">
					<div class="use-case__icon">
						<Icon name="terminal" size={24} />
					</div>
					<h3 class="use-case__title">{$_('features.invoicing.useCases.case1.title')}</h3>
					<p class="use-case__description">
						{$_('features.invoicing.useCases.case1.description')}
					</p>
				</div>
				<div class="use-case">
					<div class="use-case__icon">
						<Icon name="briefcase" size={24} />
					</div>
					<h3 class="use-case__title">{$_('features.invoicing.useCases.case2.title')}</h3>
					<p class="use-case__description">
						{$_('features.invoicing.useCases.case2.description')}
					</p>
				</div>
				<div class="use-case">
					<div class="use-case__icon">
						<Icon name="users" size={24} />
					</div>
					<h3 class="use-case__title">{$_('features.invoicing.useCases.case3.title')}</h3>
					<p class="use-case__description">
						{$_('features.invoicing.useCases.case3.description')}
					</p>
				</div>
			</div>
		</div>
	</section>

	<!-- CTA Section -->
	<section class="cta">
		<div class="container">
			<div class="cta__content">
				<h2 class="cta__title">{$_('features.invoicing.cta.title')}</h2>
				<p class="cta__subtitle">
					{$_('features.invoicing.cta.subtitle')}
				</p>
				<div class="cta__actions">
					<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
						{$_('features.invoicing.cta.primaryButton')}
					</Button>
					<Button variant="ghost" size="lg" href="/pricing">
						{$_('features.invoicing.cta.secondaryButton')}
					</Button>
				</div>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="invoicing" />

<Footer />

<style>
	.feature-page {
		min-height: 100vh;
	}

	/* Hero Section - Split layout */
	.hero {
		padding: var(--space-20) 0;
		overflow: hidden;
	}

	.hero__split {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		align-items: center;
		max-width: 1200px;
		margin: 0 auto;
	}

	.hero__eyebrow {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-1) var(--space-3);
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-full);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		margin-bottom: var(--space-4);
	}

	.hero__title {
		font-size: var(--font-size-5xl);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		margin-bottom: var(--space-8);
	}

	.hero__stats {
		display: flex;
		align-items: center;
		gap: var(--space-6);
		margin-bottom: var(--space-8);
	}

	.stat__number {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
	}

	.stat__label {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		margin-top: var(--space-1);
	}

	.stat__divider {
		width: 1px;
		height: 40px;
		background: var(--border-light);
	}

	.hero__actions {
		display: flex;
		gap: var(--space-4);
	}

	/* Invoice Preview */
	.invoice-preview {
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		padding: var(--space-8);
		box-shadow: var(--shadow-xl);
		max-width: 400px;
		margin: 0 auto;
	}

	.invoice-preview__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: var(--space-6);
		padding-bottom: var(--space-4);
		border-bottom: 2px solid var(--border-light);
	}

	.invoice-preview__logo {
		width: 60px;
		height: 60px;
		background: rgba(255, 94, 126, 0.1);
		border-radius: var(--radius-md);
	}

	.invoice-preview__number {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		font-weight: var(--font-weight-medium);
	}

	.invoice-preview__client {
		margin-bottom: var(--space-6);
	}

	.invoice-preview__line {
		height: 12px;
		background: var(--bg-secondary);
		border-radius: var(--radius-sm);
		margin-bottom: var(--space-2);
	}

	.invoice-preview__line.short {
		width: 60%;
	}

	.invoice-preview__items {
		margin-bottom: var(--space-6);
		padding: var(--space-4) 0;
		border-top: 1px solid var(--border-light);
		border-bottom: 1px solid var(--border-light);
	}

	.invoice-preview__item {
		height: 30px;
		background: linear-gradient(90deg, var(--bg-secondary) 60%, transparent 60%);
		margin-bottom: var(--space-3);
		border-radius: var(--radius-sm);
	}

	.invoice-preview__total {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: var(--space-6);
	}

	.invoice-preview__total-label {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
	}

	.invoice-preview__total-amount {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
	}

	.invoice-preview__pay-button {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--space-2);
		width: 100%;
		padding: var(--space-3);
		background: rgb(255, 94, 126);
		color: white;
		border-radius: var(--radius-md);
		font-weight: var(--font-weight-medium);
	}

	/* Workflow Section */
	.workflow {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.workflow__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.workflow__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.workflow__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
	}

	.workflow__steps {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: var(--space-4);
		max-width: 1000px;
		margin: 0 auto;
		position: relative;
	}

	.workflow__step {
		text-align: center;
		padding: var(--space-4);
		position: relative;
		transition: all 0.3s ease;
	}

	.workflow__step.active .workflow__step-icon {
		background: rgb(255, 94, 126);
		color: white;
		transform: scale(1.1);
	}

	.workflow__step-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 60px;
		height: 60px;
		background: var(--bg-primary);
		border: 2px solid var(--border-light);
		border-radius: var(--radius-full);
		margin: 0 auto var(--space-3);
		transition: all 0.3s ease;
	}

	.workflow__step-title {
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.workflow__step-description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
	}

	.workflow__arrow {
		position: absolute;
		right: -20px;
		top: 50%;
		transform: translateY(-50%);
		color: var(--text-tertiary);
	}

	/* Problems Section */
	.problems {
		padding: var(--space-20) 0;
	}

	.problems__content {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		align-items: center;
		max-width: 1100px;
		margin: 0 auto;
	}

	.problems__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-8);
		letter-spacing: -0.02em;
	}

	.problems__list {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.problem-item {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		font-size: var(--font-size-base);
		color: var(--text-secondary);
	}

	.solution-card {
		padding: var(--space-8);
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.05), transparent);
		border: 1px solid rgba(255, 94, 126, 0.2);
		border-radius: var(--radius-lg);
	}

	.solution-card__title {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.solution-card__list {
		list-style: none;
		padding: 0;
		margin: 0 0 var(--space-6) 0;
	}

	.solution-card__list li {
		padding: var(--space-3) 0;
		padding-left: var(--space-8);
		position: relative;
		color: var(--text-primary);
		line-height: 1.6;
	}

	.solution-card__list li::before {
		content: '✓';
		position: absolute;
		left: 0;
		color: rgb(255, 94, 126);
		font-weight: var(--font-weight-bold);
	}

	/* Features Section */
	.features {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.features__grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: var(--space-8);
		max-width: 1100px;
		margin: 0 auto;
	}

	.feature-block {
		text-align: center;
	}

	.feature-block__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 80px;
		height: 80px;
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		margin: 0 auto var(--space-4);
		color: rgb(255, 94, 126);
	}

	.feature-block__title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-3);
	}

	.feature-block__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* Use Cases */
	.use-cases {
		padding: var(--space-20) 0;
	}

	.use-cases__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.use-cases__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		letter-spacing: -0.02em;
	}

	.use-cases__grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: var(--space-6);
		max-width: 1100px;
		margin: 0 auto;
	}

	.use-case {
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
	}

	.use-case__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 48px;
		height: 48px;
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-md);
		margin-bottom: var(--space-4);
	}

	.use-case__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-3);
	}

	.use-case__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
		font-style: italic;
	}

	/* CTA Section */
	.cta {
		padding: var(--space-20) 0;
		text-align: center;
		background: linear-gradient(180deg, transparent 0%, var(--bg-secondary) 100%);
	}

	.cta__content {
		max-width: 700px;
		margin: 0 auto;
	}

	.cta__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.cta__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
		line-height: 1.6;
	}

	.cta__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	/* Responsive */
	@media (max-width: 768px) {
		.hero__split {
			grid-template-columns: 1fr;
		}

		.hero__visual {
			margin-top: var(--space-8);
		}

		.workflow__steps {
			grid-template-columns: 1fr;
		}

		.workflow__arrow {
			display: none;
		}

		.problems__content {
			grid-template-columns: 1fr;
		}

		.features__grid,
		.use-cases__grid {
			grid-template-columns: 1fr;
		}
	}
</style>
